import type { NoticeAction } from "./disconnect.ts";
import type { MaintenanceCountdown } from "./hud/maintenance-warning.ts";
import { queueReducer, type QueueState } from "./queue/queue-state.ts";

/**
 * The loading shell's progress (P1.4-T5): the steps the page waits on from boot to the world, how far each
 * has come, the failure the page is showing, and whether the world has been revealed. It is pure and moved
 * by events, so the bar only ever shows work the page has finished: a step is one equal share of the bar,
 * filled by the items it has in hand (the atlas pages, the character's strips, the world's chunks), and
 * nothing moves on a timer.
 *
 * The page dispatches what happened (`started`, `progressed`, `finished`, `failed`, `retried`, `revealed`,
 * and `left` when the page leaves the world it showed, P1.9-T15) and `loadingView` reads what the screen
 * shows: the bar's value over its maximum, the step it waits on and the count of finished steps.
 *
 * While the page's `hello` waits for room, the shell holds its place in the login queue (`queued`, P1.4-T164,
 * ADR-127 decision 5) and shows the queue window in place of the bar; the welcome ends it, as does anything that
 * ends the join. It also holds the maintenance schedule the gateway sent on the connection (`scheduled`), which the
 * queue window shows as a strip over it (ADR-127 decision 8).
 */

/**
 * Every step of game start, in the order the page waits on them: the page's configuration; signing in,
 * when the page has no session it can present at once (a refresh, or the login screen); the world
 * version's pointer; its manifests (the world, its atlas, its contract and the character manifest); the
 * atlas pages; the character's strips; every chunk of the world, whose collision the page predicts
 * against; the server's welcome; and the chunks covering the camera's view, painted and drawn.
 */
export const GAME_START_STEPS = ["configuration", "sign-in", "world", "manifests", "atlas", "character", "chunks", "welcome", "view"] as const;

/**
 * A step of the loading shell.
 */
export type LoadingStepId = (typeof GAME_START_STEPS)[number];

/**
 * The steps joining again does over (P1.9-T15): the server's welcome and the view drawn around the camera.
 * Everything before them, the world's load, stays in hand when the page leaves the world, so Reconnect
 * reuses it.
 */
export const REJOIN_STEPS = ["welcome", "view"] as const satisfies readonly LoadingStepId[];

/**
 * Why the page stopped, what it tells the player and what it shows beneath: the category (the sign-in, the
 * world, the server, or a connection that ended after the world was shown), the notice's title and sentence,
 * the detail line with the specifics, and what its button does. A failure before the reveal offers Retry,
 * which resumes what failed (P1.4-T5); a notice after it offers the action the disconnect table names
 * (P1.9-T15, `disconnect.ts`).
 */
export interface LoadingFailure {
  readonly category: "sign-in" | "world" | "server" | "disconnected";
  readonly title: string;
  readonly sentence: string;
  readonly detail: string;
  readonly action: "retry" | NoticeAction;
}

/**
 * One step: how many items it waits on (null until it starts, when that becomes known) and how many it
 * holds.
 */
export interface LoadingStep {
  readonly id: LoadingStepId;
  readonly total: number | null;
  readonly done: number;
}

/**
 * The shell's state: its steps, the failure shown, whether the world is revealed, the page's place in the login
 * queue while its `hello` waits for room, and the maintenance schedule the current connection was sent.
 */
export interface LoadingState {
  readonly steps: readonly LoadingStep[];
  readonly failure: LoadingFailure | null;
  readonly revealed: boolean;
  readonly queue: QueueState | null;
  readonly scheduled: MaintenanceCountdown | null;
}

/**
 * What happened: a step started with its item count, holds a number of its items, or finished; the page
 * failed or retried; the world was revealed; the page left the world, its connection closed or its
 * sign-in ended, with the notice that says why; the player departed it for character select; the gateway told the
 * page its place in the login queue, at a time on the page's clock; or it sent a maintenance schedule, null for a
 * cancel, at a time on the page's clock.
 */
export type LoadingEvent =
  | { readonly type: "started"; readonly step: LoadingStepId; readonly total: number }
  | { readonly type: "progressed"; readonly step: LoadingStepId; readonly done: number }
  | { readonly type: "finished"; readonly step: LoadingStepId }
  | { readonly type: "failed"; readonly failure: LoadingFailure }
  | { readonly type: "retried" }
  | { readonly type: "revealed" }
  | { readonly type: "left"; readonly failure: LoadingFailure }
  | { readonly type: "departed" }
  | { readonly type: "queued"; readonly place: number; readonly etaMs: number; readonly length: number; readonly now: number }
  | { readonly type: "scheduled"; readonly countdown: MaintenanceCountdown | null; readonly now: number };

/**
 * What the screen shows: the bar's value over its maximum (one per step), the step the page waits on (the
 * first not finished, or null once all are), and the finished steps as a count.
 */
export interface LoadingView {
  readonly value: number;
  readonly max: number;
  readonly step: LoadingStepId | null;
  readonly finished: number;
  readonly count: string;
}

/**
 * The steps of game start for this page.
 *
 * @param signingIn Whether the page must sign in before it can join: false for a `?token=` page and for a
 *   tab whose kept session token is still good, which the page presents at once.
 * @returns The steps in order.
 * @example
 * gameStartSteps(false);
 * // ["configuration", "world", "manifests", "atlas", "character", "chunks", "welcome", "view"]
 */
export function gameStartSteps(signingIn: boolean): readonly LoadingStepId[] {
  return GAME_START_STEPS.filter((step) => signingIn || step !== "sign-in");
}

/**
 * A shell with nothing started.
 *
 * @param steps The steps the page waits on, in order.
 * @returns The state.
 * @throws RangeError when there is no step or a step is listed twice.
 */
export function initialLoadingState(steps: readonly LoadingStepId[]): LoadingState {
  if (steps.length === 0) {
    throw new RangeError("a loading screen waits on at least one step");
  }
  if (new Set(steps).size !== steps.length) {
    throw new RangeError(`a loading screen lists each step once: ${steps.join(", ")}`);
  }
  return { steps: steps.map((id) => ({ id, total: null, done: 0 })), failure: null, revealed: false, queue: null, scheduled: null };
}

/**
 * Whether a step holds all it waits on.
 *
 * @param step The step.
 * @returns True once started with every item in hand, or finished.
 */
function isFinished(step: LoadingStep): boolean {
  return step.total !== null && step.done >= step.total;
}

/**
 * How much of its share of the bar a step fills.
 *
 * @param step The step.
 * @returns 0 before it starts, 1 when it has nothing to load, else the share of its items it holds.
 */
function fractionOf(step: LoadingStep): number {
  if (step.total === null) {
    return 0;
  }
  return step.total === 0 ? 1 : Math.min(step.done, step.total) / step.total;
}

/**
 * Replaces one step, or leaves the state alone when this screen does not list it.
 *
 * @param state The state.
 * @param id The step.
 * @param change The step's new value from its old one.
 * @returns The state with the step changed, or the same state.
 */
function withStep(state: LoadingState, id: LoadingStepId, change: (step: LoadingStep) => LoadingStep): LoadingState {
  const index = state.steps.findIndex((step) => step.id === id);
  if (index === -1) {
    return state;
  }
  const steps = state.steps.slice();
  steps[index] = change(state.steps[index] as LoadingStep);
  return { ...state, steps };
}

/**
 * Moves the shell by one event. A step that starts again after a retry keeps the items it holds while it is
 * unfinished, as a load resumes, and starts empty once it was finished, since the page is doing it again: a
 * join that failed after its welcome waits on a new welcome, and the count and the bar show that. The count
 * therefore never falls within one attempt, and falls by the steps a retry redoes. A step finished by its
 * items alone (the atlas pages, every one of which landed before a strip failed) starts empty too, and the
 * boot counts its items back in the same task, before the screen draws. Progress on a step that has not
 * started changes nothing, since its size is not known; an event for a step this screen does not list
 * changes nothing, so the page need not know which steps it shows.
 *
 * Leaving the world (`left`, P1.9-T15) shows its notice, takes the reveal back, and returns the steps a
 * rejoin does over (`REJOIN_STEPS`) to not started, so Reconnect counts up from the load it reuses to a
 * second reveal. Departing it for character select (`departed`, P1.9-T62) does the same with no notice, since the
 * player chose to leave.
 *
 * A place in the login queue (`queued`) is held only while the page joins: behind a notice or once the world is
 * shown it changes nothing. The welcome ends it, and so does a failure, a retry, leaving or departing the world, and
 * a new join, which also forgets the schedule the connection before it was sent. A schedule (`scheduled`) moves a
 * held place's clock on to its own, so the strip over the queue window counts from when the schedule was told and
 * not from the frame before it, which may be seconds older.
 *
 * @param state The state.
 * @param event What happened.
 * @returns The next state.
 * @throws RangeError when a step starts with a size that is not a whole number of items.
 */
export function loadingReducer(state: LoadingState, event: LoadingEvent): LoadingState {
  switch (event.type) {
    case "started": {
      if (!Number.isInteger(event.total) || event.total < 0) {
        throw new RangeError(`step ${event.step} waits on ${event.total} items, not a whole number`);
      }
      const joining = event.step === "welcome" && (state.queue !== null || state.scheduled !== null) ? { ...state, queue: null, scheduled: null } : state;
      return withStep(joining, event.step, (step) => ({ ...step, total: event.total, done: isFinished(step) ? 0 : Math.min(step.done, event.total) }));
    }
    case "progressed": {
      const step = state.steps.find((candidate) => candidate.id === event.step);
      if (step === undefined || step.total === null) {
        return state;
      }
      const total = step.total;
      return withStep(state, event.step, () => ({ ...step, done: Math.min(Math.max(0, event.done), total) }));
    }
    case "finished":
      return withStep(event.step === "welcome" && state.queue !== null ? { ...state, queue: null } : state, event.step, (step) => {
        const total = step.total ?? 1;
        return { ...step, total, done: total };
      });
    case "failed":
      return { ...state, failure: event.failure, queue: null };
    case "retried":
      return { ...state, failure: null, queue: null };
    case "revealed":
      return { ...state, steps: state.steps.map((step) => ({ ...step, total: step.total ?? 1, done: step.total ?? 1 })), failure: null, revealed: true, queue: null };
    case "left":
      return { ...state, steps: state.steps.map((step) => (isRejoinStep(step.id) ? { id: step.id, total: null, done: 0 } : step)), failure: event.failure, revealed: false, queue: null };
    case "departed":
      return { ...state, steps: state.steps.map((step) => (isRejoinStep(step.id) ? { id: step.id, total: null, done: 0 } : step)), failure: null, revealed: false, queue: null };
    case "queued":
      return state.failure !== null || state.revealed ? state : { ...state, queue: queueReducer(state.queue, event, event.now) };
    case "scheduled":
      return { ...state, scheduled: event.countdown, queue: state.queue === null ? null : { ...state.queue, at: Math.max(state.queue.at, event.now) } };
  }
}

/**
 * Whether joining again does a step over.
 *
 * @param id The step.
 * @returns True for the server's welcome and the view.
 */
function isRejoinStep(id: LoadingStepId): boolean {
  return (REJOIN_STEPS as readonly LoadingStepId[]).includes(id);
}

/**
 * What the screen shows for a state.
 *
 * @param state The state.
 * @returns The bar's value and maximum, the step the page waits on and the count of finished steps.
 */
export function loadingView(state: LoadingState): LoadingView {
  let value = 0;
  let finished = 0;
  let step: LoadingStepId | null = null;
  for (const entry of state.steps) {
    value += fractionOf(entry);
    if (isFinished(entry)) {
      finished += 1;
    } else if (step === null) {
      step = entry.id;
    }
  }
  return { value, max: state.steps.length, step, finished, count: `${finished}/${state.steps.length}` };
}
